<template>
  <div class="org-node" :class="{ 'org-node--root': depth === 0 }">
    <div class="org-row" :class="{ 'is-vacant': !node.user }">
      <!-- expand / collapse toggle -->
      <button
        v-if="node.children && node.children.length"
        type="button"
        class="org-toggle"
        @click.stop="open = !open"
      >
        <v-icon small>{{ open ? 'mdi-minus-box-outline' : 'mdi-plus-box-outline' }}</v-icon>
      </button>
      <span v-else class="org-toggle org-toggle--leaf"></span>

      <!-- node card -->
      <div class="org-card" @click="$emit('select', node)">
        <div class="org-card-status" :class="statusInfo.bar"></div>
        <div class="org-card-body">
          <div class="org-position">{{ node.position_name }}</div>
          <div class="org-user">
            <v-icon x-small class="mr-1" :color="node.user ? 'green darken-1' : 'red lighten-1'">
              {{ node.user ? 'mdi-account-check' : 'mdi-account-off' }}
            </v-icon>
            <span :class="{ 'red--text': !node.user }">{{ node.user ? node.user.name : 'Belum diisi' }}</span>
          </div>
        </div>
        <div class="org-actions" @click.stop>
          <v-chip x-small label class="status-chip" :color="statusInfo.chip">
            {{ statusInfo.label }}
          </v-chip>
          <v-tooltip bottom><template v-slot:activator="{ on, attrs }">
            <v-btn outlined color="primary" class="org-act-btn" v-bind="attrs" v-on="on" @click="$emit('edit', node)"><v-icon>mdi-pencil</v-icon></v-btn>
          </template><span>Edit posisi</span></v-tooltip>
          <v-tooltip bottom><template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline-flex">
              <v-btn outlined color="indigo" class="org-act-btn" :disabled="locked" @click="$emit('kpi', node)"><v-icon>mdi-target</v-icon></v-btn>
            </div>
          </template><span>{{ lockReason || 'Setup KPI' }}</span></v-tooltip>
          <v-tooltip bottom><template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline-flex">
              <v-btn outlined color="orange darken-2" class="org-act-btn" :disabled="Number(node.status_publish) !== 2" @click="$emit('accounting', node)"><v-icon>mdi-calculator-variant</v-icon></v-btn>
            </div>
          </template><span>{{ Number(node.status_publish) === 2 ? 'Adjust Accounting' : 'Aktif saat status Adjust Accounting' }}</span></v-tooltip>
          <v-tooltip v-if="!isUser" bottom><template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline-flex">
              <v-btn outlined color="error" class="org-act-btn" :disabled="locked" @click="$emit('remove', node)"><v-icon>mdi-delete</v-icon></v-btn>
            </div>
          </template><span>{{ lockReason || 'Hapus posisi' }}</span></v-tooltip>
          <v-divider v-if="!isUser" vertical class="mx-1 org-act-divider"></v-divider>
          <v-tooltip v-if="!isUser" bottom><template v-slot:activator="{ on, attrs }">
            <v-btn outlined color="deep-purple" class="org-act-btn org-act-btn--status" v-bind="attrs" v-on="on" @click="$emit('adjust-status', node)"><v-icon>mdi-swap-horizontal</v-icon></v-btn>
          </template><span>Ubah status posisi</span></v-tooltip>
        </div>
      </div>
    </div>

    <div v-if="node.children && node.children.length && open" class="org-children">
      <org-list-node
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
        :is-user="isUser"
        @select="$emit('select', $event)"
        @edit="$emit('edit', $event)"
        @kpi="$emit('kpi', $event)"
        @accounting="$emit('accounting', $event)"
        @remove="$emit('remove', $event)"
        @adjust-status="$emit('adjust-status', $event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "OrgListNode",
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    isUser: { type: Boolean, default: false },
  },
  data() {
    return { open: true };
  },
  computed: {
    locked() {
      const s = Number(this.node.status_publish);
      return s === 1 || s === 2;
    },
    lockReason() {
      const s = Number(this.node.status_publish);
      if (s === 1) return "Sudah published — tidak bisa diubah/dihapus";
      if (s === 2) return "Adjust Accounting aktif — tidak bisa diubah/dihapus";
      return "";
    },
    statusInfo() {
      switch (Number(this.node.status_publish)) {
        case 1:
          return { label: "Published", chip: "green lighten-4", bar: "st-published" };
        case 2:
          return { label: "Adjust Accounting", chip: "orange lighten-4", bar: "st-adjust" };
        default:
          return { label: "Unpublish", chip: "blue lighten-4", bar: "st-draft" };
      }
    },
  },
};
</script>

<style scoped>
.org-node { position: relative; }
.org-row { display: flex; align-items: center; padding: 2px 0; }

/* connector lines for nested nodes */
.org-children { margin-left: 26px; padding-left: 18px; border-left: 2px solid #c7d2e0; }
.org-children > .org-node { position: relative; }
.org-children > .org-node::before {
  content: "";
  position: absolute;
  top: 22px;
  left: -18px;
  width: 18px;
  height: 2px;
  background: #c7d2e0;
}
/* trim the trailing vertical line below the last child */
.org-children > .org-node:last-child::after {
  content: "";
  position: absolute;
  top: 23px;
  left: -20px;
  bottom: 0;
  width: 2px;
  background: #fff;
}

.org-toggle {
  width: 24px; height: 24px;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; cursor: pointer; flex: 0 0 24px; color: #5b7290;
}
.org-toggle--leaf { cursor: default; }

.org-card {
  flex: 1 1 auto;
  display: flex; align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 5px 10px;
  cursor: pointer;
  transition: box-shadow .15s, border-color .15s, transform .05s;
}
.org-card:hover { border-color: #2196f3; box-shadow: 0 2px 10px rgba(33,150,243,.15); }
.org-row.is-vacant .org-card { background: #fff8f6; border-color: #f3c9c0; }

.org-card-status { width: 4px; align-self: stretch; border-radius: 4px; margin-right: 12px; }
.st-published { background: #4caf50; }
.st-adjust { background: #ff9800; }
.st-draft { background: #125a97; }

.org-card-body { flex: 1 1 auto; min-width: 0; }
.org-position { font-weight: 500; color: #1f3a5f; font-size: .82rem; line-height: 1.2; }
.org-user { font-size: .72rem; color: #607d8b; display: flex; align-items: center; }

.org-actions { display: flex; align-items: center; flex: 0 0 auto; gap: 6px; }
.status-chip { margin-right: 2px; }
.org-act-btn {
  min-width: 34px !important;
  width: 34px;
  height: 34px !important;
  padding: 0 !important;
  background: #fff;
}
.org-act-btn .v-icon { font-size: 18px; }
.org-act-divider { height: 24px; }
.org-act-btn--status { background: #ede7f6; }

@media (max-width: 600px) {
  .org-children { margin-left: 12px; padding-left: 12px; }
  .org-children > .org-node::before { left: -12px; width: 12px; }
  .org-actions .v-chip { display: none; }
}
</style>
